import React from 'react';
import { Plus, Rocket, X } from 'lucide-react';

type PendingComponent = { name: string; color: string } | null;
type QueuedComponent = { name: string; color: string; referenceImage: string };

export interface AutocountConfirmModalProps {
  show: boolean;
  referenceAreaImage: string | null;
  pendingAutocountComponent: PendingComponent;
  editingQueuedName: string;
  queuedComponents: QueuedComponent[];
  electricalKey: any;
  pdfDocument: any;
  numPages: number | null;
  onKeyExtracted?: (key: any) => void;
  resolveUniqueComponentName: (name: string, existingNames: string[]) => string;
  startCountingComponents: (components: Array<{ color: string; name: string; symbol: string; referenceImage: string }>) => void;
  setEditingQueuedName: React.Dispatch<React.SetStateAction<string>>;
  setQueuedComponents: React.Dispatch<React.SetStateAction<QueuedComponent[]>>;
  setShowAutocountConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
  setReferenceAreaImage: React.Dispatch<React.SetStateAction<string | null>>;
  setAutocountSelection: React.Dispatch<React.SetStateAction<any>>;
  setPendingAutocountComponent: React.Dispatch<React.SetStateAction<any>>;
  setAutocountMode: React.Dispatch<React.SetStateAction<any>>;
}

export const AutocountConfirmModal: React.FC<AutocountConfirmModalProps> = ({
  show,
  referenceAreaImage,
  pendingAutocountComponent,
  editingQueuedName,
  queuedComponents,
  electricalKey,
  pdfDocument,
  numPages,
  onKeyExtracted,
  resolveUniqueComponentName,
  startCountingComponents,
  setEditingQueuedName,
  setQueuedComponents,
  setShowAutocountConfirmModal,
  setReferenceAreaImage,
  setAutocountSelection,
  setPendingAutocountComponent,
  setAutocountMode,
}) => {
  if (!show || !referenceAreaImage || !pendingAutocountComponent) return null;

  return (
    <div
      className="autocount-confirm-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100002,
      }}
      onClick={() => {
        setShowAutocountConfirmModal(false);
        setReferenceAreaImage(null);
      }}
    >
      <div
        className="autocount-confirm-modal"
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '16px',
          padding: '28px',
          width: '90vw',
          maxWidth: '600px',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
          border: '1px solid var(--border-color)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: '16px', flexShrink: 0 }}>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>
            Add Component
          </h3>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '13px' }}>
            Name this component, then add more or start counting.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexShrink: 0 }}>
          <div style={{
            width: '120px',
            height: '100px',
            border: '1px solid var(--border-color)',
            borderRadius: '10px',
            padding: '8px',
            background: 'var(--bg-primary)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
          }}>
            <img
              src={referenceAreaImage}
              alt="Selected reference area"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '6px',
                background: 'var(--bg-primary)',
              }}
            />
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '6px', fontWeight: 500 }}>
              Component Name
            </label>
            <input
              type="text"
              value={editingQueuedName}
              onChange={(e) => setEditingQueuedName(e.target.value)}
              placeholder="e.g., Duplex Receptacle"
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-strong)',
                borderRadius: '8px',
                color: 'var(--text-primary)',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && editingQueuedName.trim()) {
                  setQueuedComponents(prev => [...prev, {
                    name: editingQueuedName.trim(),
                    color: pendingAutocountComponent.color,
                    referenceImage: referenceAreaImage,
                  }]);
                  setShowAutocountConfirmModal(false);
                  setReferenceAreaImage(null);
                  setAutocountSelection(null);
                  setEditingQueuedName('');
                  setPendingAutocountComponent({ name: '', color: pendingAutocountComponent.color });
                }
              }}
            />
          </div>
        </div>

        {queuedComponents.length > 0 && (
          <div style={{ marginBottom: '16px', flexShrink: 0 }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px', fontWeight: 500 }}>
              Queued for counting ({queuedComponents.length})
            </label>
            <div style={{
              maxHeight: '160px',
              overflowY: 'auto',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              background: 'var(--bg-secondary)',
            }}>
              {queuedComponents.map((comp, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    borderBottom: idx < queuedComponents.length - 1 ? '1px solid var(--border-color)' : 'none',
                    transition: 'background 0.15s',
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <img
                    src={comp.referenceImage}
                    alt={comp.name}
                    style={{
                      width: '36px',
                      height: '36px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      border: `2px solid ${comp.color}`,
                      flexShrink: 0,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <span style={{ flex: 1, fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {comp.name}
                  </span>
                  <button
                    onClick={() => {
                      setQueuedComponents(prev => prev.filter((_, i) => i !== idx));
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-placeholder)',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-placeholder)'; }}
                    title="Remove"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button
            onClick={() => {
              setShowAutocountConfirmModal(false);
              setReferenceAreaImage(null);
              setAutocountSelection(null);
            }}
            style={{
              padding: '10px 16px',
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-strong)',
              borderRadius: '8px',
              color: 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-divider)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-tertiary)'; }}
          >
            Reselect
          </button>
          <button
            onClick={() => {
              if (!editingQueuedName.trim()) return;
              setQueuedComponents(prev => [...prev, {
                name: editingQueuedName.trim(),
                color: pendingAutocountComponent.color,
                referenceImage: referenceAreaImage,
              }]);
              setShowAutocountConfirmModal(false);
              setReferenceAreaImage(null);
              setAutocountSelection(null);
              setEditingQueuedName('');
              setPendingAutocountComponent({ name: '', color: pendingAutocountComponent.color });
            }}
            disabled={!editingQueuedName.trim()}
            style={{
              padding: '10px 16px',
              background: editingQueuedName.trim() ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
              border: editingQueuedName.trim() ? '2px solid #22c55e' : '1px solid var(--border-strong)',
              borderRadius: '8px',
              color: editingQueuedName.trim() ? '#16a34a' : 'var(--text-placeholder)',
              fontSize: '13px',
              fontWeight: 600,
              cursor: editingQueuedName.trim() ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
            onMouseEnter={(e) => {
              if (editingQueuedName.trim()) {
                e.currentTarget.style.background = '#f0fdf4';
              }
            }}
            onMouseLeave={(e) => {
              if (editingQueuedName.trim()) {
                e.currentTarget.style.background = 'var(--bg-primary)';
              }
            }}
          >
            <Plus size={14} />
            Add & Scan Next
          </button>
          <button
            onClick={() => {
              if (!editingQueuedName.trim() && queuedComponents.length === 0) return;

              const allComponents = [...queuedComponents];
              if (editingQueuedName.trim()) {
                allComponents.push({
                  name: editingQueuedName.trim(),
                  color: pendingAutocountComponent.color,
                  referenceImage: referenceAreaImage,
                });
              }

              if (allComponents.length === 0) return;

              const existingComponents = electricalKey?.components || [];
              const existingNames = existingComponents.map((c: any) => c.name);

              const newKeyComponents = allComponents.map(comp => {
                const uniqueName = resolveUniqueComponentName(comp.name, existingNames);
                existingNames.push(uniqueName);
                return {
                  name: uniqueName,
                  color: comp.color,
                  symbol: comp.name,
                  referenceImage: comp.referenceImage,
                  pages: [] as any[],
                  totalCount: 0,
                  hasReferenceImage: true,
                };
              });

              const updatedKey = {
                ...(electricalKey || {}),
                components: [...existingComponents, ...newKeyComponents],
                extractedAt: (electricalKey as any)?.extractedAt || new Date().toISOString(),
              };

              if (onKeyExtracted) {
                onKeyExtracted(updatedKey);
              }

              setShowAutocountConfirmModal(false);
              setAutocountMode(null);
              setReferenceAreaImage(null);
              setAutocountSelection(null);
              setEditingQueuedName('');
              setQueuedComponents([]);

              if (pdfDocument && numPages) {
                startCountingComponents(allComponents.map(c => ({
                  color: c.color,
                  name: c.name,
                  symbol: c.name,
                  referenceImage: c.referenceImage,
                })));
              }
            }}
            disabled={!editingQueuedName.trim() && queuedComponents.length === 0}
            style={{
              padding: '10px 20px',
              background: (editingQueuedName.trim() || queuedComponents.length > 0)
                ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                : 'var(--border-strong)',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: (editingQueuedName.trim() || queuedComponents.length > 0) ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: (editingQueuedName.trim() || queuedComponents.length > 0)
                ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
            }}
            onMouseEnter={(e) => {
              if (editingQueuedName.trim() || queuedComponents.length > 0) {
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Rocket size={14} />
            Count All ({queuedComponents.length + (editingQueuedName.trim() ? 1 : 0)})
          </button>
        </div>
      </div>
    </div>
  );
};
